import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { DeferredChatWidget } from "@/components/DeferredChatWidget";
import { MetaPixelRouteTracker } from "@/components/MetaPixelRouteTracker";

// Eager load the main page for faster LCP
import Index from "./pages/Index";
import { SchemaGraphProvider } from "@/components/SchemaGraph";

// Lazy load other pages to reduce initial bundle
const About = lazy(() => import("./pages/About"));
const Services = lazy(() => import("./pages/Services"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Contact = lazy(() => import("./pages/Contact"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Cookies = lazy(() => import("./pages/Cookies"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Auth = lazy(() => import("./pages/Auth"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Compare = lazy(() => import("./pages/Compare"));
const ComparePitchbox = lazy(() => import("./pages/ComparePitchbox"));
const CompareBuzzStream = lazy(() => import("./pages/CompareBuzzStream"));
const CompareManual = lazy(() => import("./pages/CompareManual"));
const Industries = lazy(() => import("./pages/Industries"));
const FinTechIndustry = lazy(() => import("./pages/industries/FinTech"));
const InsuranceIndustry = lazy(() => import("./pages/industries/Insurance"));
const SaaSIndustry = lazy(() => import("./pages/industries/SaaS"));
const EducationIndustry = lazy(() => import("./pages/industries/Education"));
const GamblingIndustry = lazy(() => import("./pages/industries/Gambling"));
const BlogAdmin = lazy(() => import("./pages/BlogAdmin"));
const IndexingStatus = lazy(() => import("./pages/IndexingStatus"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
import { canonicalRoute, redirectEntries } from "./lib/internal-links";
const B2BLinkBuilding = lazy(() => import("./pages/B2BLinkBuilding"));
const GamblingLinkBuilding = lazy(() => import("./pages/GamblingLinkBuilding"));
const AIVisibilityAudit = lazy(() => import("./pages/AIVisibilityAudit"));
const GuestPostService = lazy(() => import("./pages/GuestPostService"));
const AIVisibilityChecker = lazy(() => import("./pages/AIVisibilityChecker"));
const AeoGeoServices = lazy(() => import("./pages/AeoGeoServices"));
const SEOServicePage = lazy(() => import("./pages/SEOServicePage"));
const BestLinkBuildingServices = lazy(() => import("./pages/BestLinkBuildingServices"));


const queryClient = new QueryClient();

const LegacyBlogPostRedirect = () => {
  const { slug } = useParams<{ slug: string }>();

  return <Navigate to={slug ? canonicalRoute(`/blog/${slug}`) : "/blog"} replace />;
};

// Minimal loading fallback to prevent layout shift
const PageLoader = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <SchemaGraphProvider>
        <MetaPixelRouteTracker />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/about" element={<About />} />
            <Route path="/services" element={<Services />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/cookies" element={<Cookies />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/post/:slug" element={<LegacyBlogPostRedirect />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/industries" element={<Industries />} />
            <Route path="/industries/fintech" element={<FinTechIndustry />} />
            <Route path="/industries/insurance" element={<InsuranceIndustry />} />
            <Route path="/industries/saas" element={<SaaSIndustry />} />
            <Route path="/industries/education" element={<EducationIndustry />} />
            <Route path="/industries/gambling" element={<GamblingIndustry />} />
            <Route path="/compare" element={<Compare />} />
            <Route path="/compare/best-link-building-services" element={<BestLinkBuildingServices />} />
            <Route path="/compare/linkzy-vs-pitchbox" element={<ComparePitchbox />} />
            <Route path="/compare/linkzy-vs-buzzstream" element={<CompareBuzzStream />} />
            <Route path="/compare/linkzy-vs-manual-link-building" element={<CompareManual />} />
            {/* Redirects for old URLs */}
            {redirectEntries.map(([from, to]) => (
              <Route key={from} path={from} element={<Navigate to={to} replace />} />
            ))}
            {/* Free tools removed */}
            <Route path="/blog-admin" element={<BlogAdmin />} />
            <Route path="/indexing-status" element={<IndexingStatus />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="/b2b-link-building" element={<B2BLinkBuilding />} />
            <Route path="/gambling-link-building" element={<GamblingLinkBuilding />} />
            <Route path="/ai-visibility-audit" element={<AIVisibilityAudit />} />
            <Route path="/guest-post-service" element={<GuestPostService />} />
            <Route path="/ai-visibility-checker" element={<AIVisibilityChecker />} />
            <Route path="/aeo-geo-services" element={<AeoGeoServices />} />
            <Route path="/white-label-link-building" element={<SEOServicePage />} />
            <Route path="/casino-link-building" element={<SEOServicePage />} />
            <Route path="/crypto-link-building" element={<SEOServicePage />} />
            <Route path="/saas-link-building" element={<SEOServicePage />} />

            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        <DeferredChatWidget />
        </SchemaGraphProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
